import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  CreditCard,
  Check,
  Calendar,
  Clock,
  AlertCircle,
  MapPin,
  Navigation,
  Star,
  Shield,
  Timer,
  X
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { Header } from '../components/Layout/Header'
import { subscriptionService, TrialStatusResponse } from '../services/subscriptionService'
import AnalyticsService from '../services/analyticsService'
import { SUPPORT_EMAIL } from '../constants/contact'
import emailService from '../services/emailService'

const Subscription = () => {
  const { user, subscription, checkSubscription } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [paymentResult, setPaymentResult] = useState<{
    type: 'success' | 'error'
    title: string
    message: string
    details?: Record<string, string>
  } | null>(null)
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [paymentFormDisplayed, setPaymentFormDisplayed] = useState(false)
  const [trialStatus, setTrialStatus] = useState<TrialStatusResponse | null>(null)
  const [currentTrialSubscriptionId, setCurrentTrialSubscriptionId] = useState<string | null>(null)
  const [currentFormToken, setCurrentFormToken] = useState<string | null>(null)
  const [paymentSuccess, setPaymentSuccess] = useState(false)

  useEffect(() => {
    loadSubscriptionInfo()
    loadSystemPayScript()
  }, [])

  // Redirection automatique seulement si l'utilisateur arrive depuis une autre page avec un abonnement actif
  // Mais permettre l'accès direct à la page d'abonnement pour voir le statut
  useEffect(() => {
    if (!loading && subscription && subscription.status === 'active') {
      // Pour les abonnements actifs, permettre la visualisation du statut sur cette page
      // Ne pas rediriger automatiquement
      console.log('👁️ Utilisateur avec abonnement actif - affichage du statut sur la page d\'abonnement')
    }
  }, [loading, subscription])

  const loadSystemPayScript = () => {
    // Charger le script SystemPay
    if (!document.querySelector('script[src*="kr-payment-form"]')) {
      const script = document.createElement('script')
      script.src = 'https://static.systempay.fr/static/js/krypton-client/V4.0/stable/kr-payment-form.min.js'
      script.setAttribute('kr-public-key', '67361502:publickey_YK1k7LNpmzU6usYlVGXEO4WOMAsezDDdgnongY2ErOFhe')
      script.setAttribute('kr-language', 'en-GB')
      document.head.appendChild(script)

      // Charger le thème Neon
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = 'https://static.systempay.fr/static/js/krypton-client/V4.0/ext/neon-reset.min.css'
      document.head.appendChild(link)

      const neonScript = document.createElement('script')
      neonScript.src = 'https://static.systempay.fr/static/js/krypton-client/V4.0/ext/neon.js'
      document.head.appendChild(neonScript)
    }
  }

  const loadSubscriptionInfo = async (forceRefresh: boolean = false) => {
    try {
      setLoading(true)
      await checkSubscription(forceRefresh)
    } catch (error) {
      console.error('❌ Erreur lors du chargement de l\'abonnement:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubscribe = async () => {
    if (!user?.email) return

    try {
      setPaymentLoading(true)
      setPaymentResult(null)

      console.log('🚀 Début de la création d\'abonnement')

      const result = await subscriptionService.createSubscriptionWithPayment(user.email, user.id)

      if (result.success && result.data) {
        setCurrentOrderId(result.data.orderId)
        
        // Afficher le formulaire de paiement SystemPay
        await displayPaymentForm(result.data.formToken)
        setShowPaymentForm(true)
        
        // Démarrer la surveillance du paiement
        startPaymentPolling(result.data.orderId)
        
      } else {
        throw new Error(result.message || 'Erreur lors de la création du paiement')
      }

    } catch (error) {
      console.error('❌ Erreur:', error)
      setPaymentResult({
        type: 'error',
        title: 'Erreur',
        message: error instanceof Error ? error.message : 'Une erreur est survenue'
      })
    } finally {
      setPaymentLoading(false)
    }
  }

  const displayPaymentForm = async (formToken: string) => {
    try {
      // Attendre que le script SystemPay soit chargé
      await new Promise<void>((resolve) => {
        const checkKR = () => {
          if (window.KR) {
            resolve()
          } else {
            setTimeout(checkKR, 100)
          }
        }
        checkKR()
      })

      if (window.KR) {
        await window.KR.setFormConfig({
          formToken: formToken,
          'kr-language': 'en-GB'
        })

        const container = document.getElementById('payment-container')
        if (container) {
          container.innerHTML = '<div class="kr-smart-form"></div>'
          await window.KR.renderElements('#payment-container')
          
          // Marquer que le formulaire est affiché
          setPaymentFormDisplayed(true)
        }
      }

    } catch (error) {
      console.error('❌ Erreur lors de l\'affichage du formulaire:', error)
      throw error
    }
  }

  const startPaymentPolling = (orderId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const result = await subscriptionService.getTransactionStatus(orderId)

        if (result.success && result.data) {
          const status = result.data.status

          if (status === 'completed') {
            clearInterval(pollInterval)
            setPaymentResult({
              type: 'success',
              title: 'Payment Successful!',
              message: 'Your subscription has been created successfully.',
              details: {
                'Amount': `$${result.data.amount / 100}`,
                'Plan': 'Premium Parking',
                'Frequency': 'Monthly'
              }
            })

            // Marquer que le paiement a réussi pour éviter l'affichage de la section d'essai
            setPaymentSuccess(true)

            // Recharger les informations d'abonnement (forcer le refresh)
            await loadSubscriptionInfo(true)
            setShowPaymentForm(false)

            // Rediriger vers le dashboard après 2 secondes
            setTimeout(() => {
              navigate('/dashboard')
            }, 2000)
            
          } else if (status === 'failed') {
            clearInterval(pollInterval)
            setPaymentResult({
              type: 'error',
              title: 'Payment Failed',
              message: 'An error occurred while processing your payment.'
            })
            
          } else if (status === 'cancelled') {
            clearInterval(pollInterval)
            setPaymentResult({
              type: 'error',
              title: 'Payment Cancelled',
              message: 'The payment was cancelled.'
            })
          }
        }
      } catch (error) {
        console.error('❌ Erreur lors de la vérification du statut:', error)
      }
    }, 2000)

    // Arrêter le polling après 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval)
    }, 300000)
  }

  const handleStartTrial = async () => {
    if (!user?.email) return

    try {
      setPaymentLoading(true)
      setPaymentResult(null)

      console.log('🚀 Starting trial subscription for:', user.email)
      
      // Tracker le début de l'essai
      AnalyticsService.trackEvent('begin_checkout', {
        event_category: 'subscription',
        event_label: 'trial_start',
        currency: 'USD',
        value: 1, // $1 verification charge
      })

      // Récupérer le nom complet de l'utilisateur
      const fullName = user.user_metadata?.full_name || user.email.split('@')[0]
      console.log('👤 Nom utilisateur:', fullName)

      const result = await subscriptionService.createTrialSubscription(user.email, fullName)

      if (result.success && result.data) {
        setCurrentOrderId(result.data.orderId)
        setCurrentTrialSubscriptionId(result.data.subscriptionId)
        setCurrentFormToken(result.data.formToken)
        
        // Show payment form section
        setShowPaymentForm(true)
        
        // Display payment form directly
        console.log('🔧 Displaying payment form directly with token:', result.data.formToken)
        await displayPaymentForm(result.data.formToken)
        
        // Start payment polling
        startTrialPaymentPolling(result.data.orderId)
        
      } else {
        throw new Error(result.message || 'Error creating trial subscription')
      }

    } catch (error) {
      console.error('❌ Error:', error)
      
      // Tracker l'erreur de création d'essai
      AnalyticsService.trackError('trial_creation_error', error instanceof Error ? error.message : 'Unknown trial creation error')
      
      setPaymentResult({
        type: 'error',
        title: 'Error',
        message: error instanceof Error ? error.message : 'An error occurred'
      })
    } finally {
      setPaymentLoading(false)
    }
  }

  const startTrialPaymentPolling = (orderId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const result = await subscriptionService.getTransactionStatus(orderId)

        if (result.success && result.data) {
          const status = result.data.status

          if (status === 'completed') {
            clearInterval(pollInterval)

            // Tracker le succès de l'abonnement d'essai
            AnalyticsService.trackSubscription('trial')
            AnalyticsService.trackEvent('purchase', {
              event_category: 'subscription',
              event_label: 'trial_success',
              currency: 'USD',
              value: 1,
            })

            // Envoyer l'email de bienvenue d'essai de manière asynchrone
            if (user?.email) {
              console.log('📧 Envoi de l\'email de bienvenue d\'essai pour:', user.email)
              emailService.sendTrialWelcomeEmailAsync(user.email, user.email.split('@')[0])
                .catch(error => {
                  console.warn('⚠️ Erreur lors de l\'envoi de l\'email de bienvenue (non bloquant):', error)
                })
            }

            setPaymentResult({
              type: 'success',
              title: 'Trial Activated!',
              message: 'Your 24-hour free trial has started.',
              details: {
                'Trial Duration': '24 hours',
                'Verification': '$1.00 (automatically refunded)',
                'After Trial': '$49.99/month'
              }
            })

            // Marquer que le paiement a réussi pour éviter l'affichage de la section d'essai
            setPaymentSuccess(true)

            // Reload subscription info (forcer le refresh)
            await loadSubscriptionInfo(true)
            setShowPaymentForm(false)

            // Rediriger vers le dashboard après 2 secondes
            setTimeout(() => {
              navigate('/dashboard')
            }, 2000)
            
          } else if (status === 'failed') {
            clearInterval(pollInterval)
            
            // Tracker l'échec du paiement
            AnalyticsService.trackError('payment_failed', 'Trial payment verification failed')
            
            setPaymentResult({
              type: 'error',
              title: 'Payment Verification Failed',
              message: 'We could not verify your credit card. Please try again.'
            })
            
          } else if (status === 'cancelled') {
            clearInterval(pollInterval)
            
            // Tracker l'annulation du paiement
            AnalyticsService.trackEvent('payment_cancelled', {
              event_category: 'subscription',
              event_label: 'trial_payment_cancelled',
            })
            
            setPaymentResult({
              type: 'error',
              title: 'Payment Cancelled',
              message: 'The payment verification was cancelled.'
            })
          }
        }
      } catch (error) {
        console.error('❌ Error checking payment status:', error)
      }
    }, 2000)

    // Stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval)
    }, 300000)
  }

  const handleCancelSubscription = async () => {
    if (!subscription || !user?.email) return

    const subscriptionType = subscription.status === 'trial' ? 'trial' : 'subscription'
    const confirmMessage = `Are you sure you want to cancel your ${subscriptionType}?

${subscription.status === 'trial' 
  ? 'Your trial will end immediately' 
  : 'Your access will remain active until the end of your current billing period.\nNo refund will be provided.'
}

This action is irreversible.`

    if (!window.confirm(confirmMessage)) {
      return
    }

    try {
      setCancelLoading(true)
      console.log('🚫 Début de l\'annulation pour:', user.email)
      
      // Utiliser la nouvelle méthode qui récupère automatiquement l'ID
      const result = await subscriptionService.cancelSubscription(user.email, true)
      
      if (result.success) {
        // Tracker l'annulation d'abonnement
        AnalyticsService.trackEvent('subscription_cancelled', {
          event_category: 'subscription',
          event_label: subscription.status === 'trial' ? 'trial_cancelled' : 'subscription_cancelled',
        })
        
        // Envoyer l'email d'annulation de manière asynchrone
        if (user?.email) {
          console.log('📧 Envoi de l\'email d\'annulation pour:', user.email, 'Type:', subscriptionType)
          emailService.sendCancellationEmailAsync(
            user.email, 
            subscriptionType as 'trial' | 'subscription',
            user.email.split('@')[0],
            subscription.expiryDate
          ).catch(error => {
            console.warn('⚠️ Erreur lors de l\'envoi de l\'email d\'annulation (non bloquant):', error)
          })
        }
        
        const successMessage = subscription.status === 'trial' 
          ? 'Your trial has been cancelled successfully.\n\n'
          : 'Your subscription has been cancelled successfully.\n\nYour access remains active until the end of your billing period.'
        
        alert(successMessage)
        
        // Recharger les informations d'abonnement (forcer le refresh)
        await loadSubscriptionInfo(true)
        
        // Rediriger vers la page d'accueil après 2 secondes
        setTimeout(() => {
          navigate('/')
        }, 2000)
        
      } else {
        throw new Error(result.message || 'Error cancelling subscription')
      }

    } catch (error) {
      console.error('❌ Erreur lors de l\'annulation:', error)
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      alert(`An error occurred while cancelling your ${subscriptionType}: ${errorMessage}`)
      
      // Si l'erreur indique qu'aucun abonnement n'a été trouvé, recharger les infos
      if (errorMessage.includes('Aucun abonnement trouvé') || errorMessage.includes('No subscription found')) {
        console.log('🔄 Rechargement des informations d\'abonnement après erreur')
        await loadSubscriptionInfo()
      }
      
    } finally {
      setCancelLoading(false)
    }
  }

  const getStatusInfo = () => {
    if (!subscription) return { status: 'none', message: '', color: 'gray' }
    
    if (subscription.status === 'active') {
      return {
        status: 'active',
        message: 'Active',
        color: 'green'
      }
    }
    
    if (subscription.status === 'trial') {
      return {
        status: 'trial',
        message: `Free Trial - ${subscription.daysUntilNext} day(s) remaining`,
        color: 'blue'
      }
    }
    
    if (subscription.status === 'cancelled') {
      return {
        status: 'cancelled',
        message: `Cancelled - Active until ${subscription.expiryDate}`,
        color: 'orange'
      }
    }
    
    return {
      status: subscription.status,
      message: subscription.status,
      color: 'gray'
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header />
      
      {/* Main content */}
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Affichage de l'abonnement d'essai actif */}
        {subscription && subscription.status === 'trial' && !paymentSuccess && (
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="p-6">
              <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Timer className="w-8 h-8 text-blue-600" />
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">
                    Free Trial Active
                  </h4>
                  <div className="text-center mb-4">
                    <div className="text-lg font-semibold text-blue-600 mb-1">
                      {subscription.daysUntilNext} day(s) remaining
                    </div>
                    <div className="text-sm text-gray-600">
                      Trial ends on {subscription.trialEndDate ? new Date(subscription.trialEndDate).toLocaleDateString() : subscription.expiryDate}
                    </div>
                  </div>
                  
                  {/* Trial benefits */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                      <div className="text-left">
                        <h5 className="text-sm font-medium text-green-900 mb-1">
                          Full Premium Access
                        </h5>
                        <p className="text-sm text-green-700">
                          You have complete access to all premium features during your trial period.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center">
                    <MapPin className="h-5 w-5 text-green-600 mr-3" />
                    <span className="text-gray-700">Interactive parking map</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-green-600 mr-3" />
                    <span className="text-gray-700">Real-time availability</span>
                  </div>
                  <div className="flex items-center">
                    <Navigation className="h-5 w-5 text-green-600 mr-3" />
                    <span className="text-gray-700">GPS navigation</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-green-600 mr-3" />
                    <span className="text-gray-700">Save favorites</span>
                  </div>
                </div>

                <div className="text-center">
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelLoading}
                    className="bg-red-600 text-white py-2 px-6 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 mr-4"
                  >
                    {cancelLoading ? 'Cancelling...' : 'Cancel Trial'}
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    Cancel anytime during trial at no charge
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pas d'abonnement ou abonnement expiré */}
        {(!subscription || (subscription.status !== 'active' && subscription.status !== 'trial')) && (
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="p-6">
              {!showPaymentForm ? (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Timer className="w-8 h-8 text-green-600" />
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 mb-2">
                    </h4>
                    <div className="text-center mb-6">
                      <div className="text-3xl font-bold text-green-600 mb-1">
                        Welcome
                      </div>
                      <div className="text-sm text-gray-600 bg-green-100 inline-block px-3 py-1 rounded-full">
                        ✨ Free trial • Cancel anytime
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center">
                      <MapPin className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">Interactive parking map</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">Real-time availability</span>
                    </div>
                    <div className="flex items-center">
                      <Navigation className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">GPS navigation</span>
                    </div>
                    <div className="flex items-center">
                      <Check className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">Full access during trial</span>
                    </div>
                    <div className="flex items-center">
                      <X className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">Cancel anytime during trial</span>
                    </div>
                  </div>

                  <div className="text-center">
                    <button
                      onClick={handleStartTrial}
                      disabled={paymentLoading}
                      className="bg-green-600 text-white py-3 px-8 rounded-lg text-lg font-medium hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
                    >
                      {paymentLoading ? 'Processing...' : 'CONTINUE'}
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      Card verification required for activation • Cancel anytime during trial at no charge
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-4 text-center">
                    <p className="text-gray-700 text-sm font-bold">
                      Please verify your payment method to activate your account and access the App.
                    </p>
                  </div>
                  
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div>
                      <h4 className="font-medium text-green-900">Card Verification Required</h4>
                      <p className="text-sm text-green-700">We need to verify your credit card with a small amount to activate your account and secure future payments.</p>
                      <p className="text-sm text-green-800 font-medium mt-2">
                        A $1 verification charge will be temporarily placed on your card to confirm it's valid and will be automatically refunded within 24-48 hours. This verification is required to activate your account.
                      </p>
                    </div>
                  </div>

                  {paymentLoading && (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading payment form...</p>
                    </div>
                  )}

                  <div id="payment-container" className="min-h-[400px]">
                    {/* Le formulaire SystemPay sera injecté ici */}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Résultat du paiement */}
        {paymentResult && (
          <div className={`mb-8 p-4 rounded-lg ${
            paymentResult.type === 'success' 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start">
              {paymentResult.type === 'success' ? (
                <Check className="h-5 w-5 text-green-400 mr-3 mt-0.5" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
              )}
              <div>
                <h4 className={`text-sm font-medium ${
                  paymentResult.type === 'success' ? 'text-green-900' : 'text-red-900'
                }`}>
                  {paymentResult.title}
                </h4>
                <p className={`text-sm mt-1 ${
                  paymentResult.type === 'success' ? 'text-green-700' : 'text-red-700'
                }`}>
                  {paymentResult.message}
                </p>
                {paymentResult.details && (
                  <div className="mt-2 text-sm">
                    {Object.entries(paymentResult.details).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="font-medium">{key}:</span>
                        <span>{value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Informations de sécurité */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Security & Billing
            </h3>
          </div>
          <div className="p-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <Shield className="h-5 w-5 text-blue-400 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-blue-900">
                    Secure Payment
                  </h4>
                  <p className="text-sm text-blue-700 mt-1">
                    All payments are securely processed by SystemPay. 
                    Your credit card information is never stored on our servers.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  Why do I need to provide my credit card for the free trial?
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  We perform a $1 verification charge on your card to activate your account and ensure your payment method is valid. This amount is automatically refunded within 24-48 hours. This step is necessary to secure your account and enable access to the application.
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  How does the 24-hour free trial work?
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  Start your free trial with just a $1 verification charge (automatically refunded). 
                  Enjoy full access for 24 hours, then your subscription automatically continues at $49.99/month.
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  What happens after the trial?
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  After your 24-hour free trial ends, your subscription automatically continues at $49.99/month. 
                  You'll be charged monthly until you cancel your subscription.
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  Can I cancel during the trial?
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  Yes! Cancel anytime during your 24-hour trial at no charge. 
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  Can I cancel my subscription anytime?
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  Yes, you can cancel your subscription at any time after the trial. 
                  You'll retain access until the end of your current billing period.
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  What happens after I cancel?
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  You'll keep access to all premium features until your subscription expires. 
                  After that, you'll return to the free tier with limited features.
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  Need help with billing or subscription?
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  Contact our support team at{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-paybyphone-green hover:text-green-600 font-medium">
                    {SUPPORT_EMAIL}
                  </a>
                  {' '}for any questions about your subscription, billing, or account.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Premium Benefits Section */}
        <div className="bg-white shadow rounded-lg mt-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Why Choose Our App?
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* Time & Money Savings */}
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <Clock className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      Save Time & Money Every Day
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Stop wasting precious minutes circling blocks looking for parking. Our premium users save an average of 15-20 minutes per trip and avoid costly parking tickets. With real-time availability updates, you'll know exactly where to go before you even leave your destination.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <MapPin className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      Interactive Smart Map
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Access our advanced interactive map with detailed parking information, including pricing, time limits, and accessibility features. Filter by your preferences and see exactly what's available in your area with color-coded availability indicators.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <Navigation className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      GPS Navigation Integration
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Get turn-by-turn directions directly to available parking spots. Our system integrates seamlessly with your preferred navigation app, ensuring you reach your destination efficiently without the stress of searching for parking.
                    </p>
                  </div>
                </div>
              </div>

              {/* Comfort & Peace of Mind */}
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <Star className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      Save Your Favorite Spots
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Create a personalized list of your most-used parking locations. Whether it's near your office, favorite restaurant, or gym, quickly access your saved spots and get real-time availability updates for the places you visit most.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      Smart Notifications & Alerts
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Receive intelligent notifications about parking meter expiration, street cleaning schedules, and special events that might affect parking availability. Never get caught off-guard by parking restrictions again.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <Shield className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      Priority Support & Updates
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Enjoy priority customer support and be the first to access new features and improvements. Our premium members get exclusive early access to beta features and direct feedback channels to help shape the future of our service.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Statistics Section */}
            <div className="mt-8 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
              <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">
                Join Thousands of Satisfied Premium Users
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-center mb-6">
                <div>
                  <div className="text-3xl font-bold text-green-600 mb-1">15-20 min</div>
                  <div className="text-sm text-gray-600">Average time saved per trip</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-600 mb-1">98%</div>
                  <div className="text-sm text-gray-600">Customer satisfaction rate</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-600 mb-1">$200+</div>
                  <div className="text-sm text-gray-600">Average yearly savings on tickets</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-orange-600 mb-1">$49.99</div>
                  <div className="text-sm text-gray-600">Monthly subscription price</div>
                </div>
              </div>
              
              {/* Call to Action Button */}
              <div className="text-center">
                <button
                  onClick={async () => {
                    // Scroll to the very top of the page (including header)
                    document.documentElement.scrollTop = 0
                    document.body.scrollTop = 0
                    // Small delay to ensure scroll completes before starting trial
                    setTimeout(() => {
                      handleStartTrial()
                    }, 100)
                  }}
                  disabled={paymentLoading}
                  className="bg-green-600 text-white py-3 px-8 rounded-lg text-lg font-medium hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
                >
                  {paymentLoading ? 'Processing...' : 'CONTINUE'}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Cancel anytime during trial at no charge
                </p>
              </div>
            </div>

            {/* Call to Action */}
            <div className="mt-6 text-center">
              <p className="text-gray-700 text-lg mb-4">
                <strong>Transform your parking experience today.</strong> Stop the frustration of endless searching and start enjoying stress-free parking with our premium features.
              </p>
              <p className="text-sm text-gray-600">
                With our 24-hour free trial, you can experience all these benefits risk-free. 
                Cancel anytime during the trial period at no charge.
              </p>
            </div>
          </div>
        </div>

        {/* Terms acceptance notice */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            By subscribing to our service, you agree to our{' '}
            <Link to="/terms" className="text-paybyphone-green hover:underline">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-paybyphone-green hover:underline">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
        </div>
      </div>
    </div>
  )
}

export default Subscription
